/**
 * use-platform-promo.ts — Hooks cho Platform → Mã ưu đãi (super-admin).
 * Backend: ecdp_backend_source/src/modules/billing/promo-admin-routes.ts
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { platformApi } from '@/lib/platform-client'
import type { PromoCodeInput, PromoCodeItem, PromoRedemptionItem } from '@/pages/platform/promo-editor'

const KEY = ['platform', 'promo-codes'] as const

export function usePromoCodes() {
  return useQuery<PromoCodeItem[]>({
    queryKey: KEY,
    queryFn: async () => {
      const { data } = await platformApi.get<{ promoCodes: PromoCodeItem[] }>('/platform/promo-codes')
      return data.promoCodes
    },
  })
}

export function usePromoRedemptions(id: string | null) {
  return useQuery<PromoRedemptionItem[]>({
    queryKey: [...KEY, id, 'redemptions'],
    enabled: !!id,
    queryFn: async () => {
      const { data } = await platformApi.get<{ redemptions: PromoRedemptionItem[] }>(`/platform/promo-codes/${id}/redemptions`)
      return data.redemptions
    },
  })
}

export async function generatePromoCode(prefix = ''): Promise<string> {
  const { data } = await platformApi.get<{ code: string }>('/platform/promo-codes/generate', { params: { prefix } })
  return data.code
}

export function useSavePromoCode() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, input }: { id?: string; input: PromoCodeInput }) => {
      const { data } = id
        ? await platformApi.put<PromoCodeItem>(`/platform/promo-codes/${id}`, input)
        : await platformApi.post<PromoCodeItem>('/platform/promo-codes', input)
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  })
}

export function useTogglePromoCode() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, isActive }: { id: string; isActive: boolean }) => {
      const { data } = await platformApi.patch<PromoCodeItem>(`/platform/promo-codes/${id}`, { isActive })
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  })
}

export function useDeletePromoCode() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await platformApi.delete<{ deleted: boolean; deactivated: boolean }>(`/platform/promo-codes/${id}`)
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  })
}
